import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

import { SYSTEM_COLORS } from "../../system.colors"

export const RetailSelect = React.memo(({
  options = [],
  value,
  onChange,
  width = "100%",
  disabled = false,
  multiple = true }) => {

  const [isOpen, setIsOpen] = useState(false);
  const [panelVisible, setPanelVisible] = useState(false);
  const [hoveredOption, setHoveredOption] = useState(null);
  const wrapperRef = useRef(null);
  const portalPanelRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const clickedWrapper = wrapperRef.current && wrapperRef.current.contains(e.target);
      const clickedPortalPanel = portalPanelRef.current && portalPanelRef.current.contains(e.target);
      if (!clickedWrapper && !clickedPortalPanel) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      const raf = requestAnimationFrame(() => setPanelVisible(true));
      return () => cancelAnimationFrame(raf);
    }
    setPanelVisible(false);
  }, [isOpen]);

  const retailWidth = width && width !== "100%" ? width : 220;
  const selectedValues = Array.isArray(value) ? value : [];
  const individualOptions = options.filter(opt => opt.value !== 0);
  const allIndividualSelected = multiple && individualOptions.length > 0 &&
    individualOptions.every(opt => selectedValues.includes(opt.value));

  const selectedLabels = allIndividualSelected
    ? "Todos selecionados"
    : options
      .filter(opt => selectedValues.includes(opt.value))
      .map(opt => opt.label)
      .join(", ");

  const toggle = (optValue) => {
    if (disabled) return;
    if (!multiple) {
      onChange([optValue]);
      setIsOpen(false);
      return;
    }
    const next = selectedValues.includes(optValue)
      ? selectedValues.filter(v => v !== optValue)
      : [...selectedValues, optValue];
    onChange(next);
  };

  return (
    <div ref={wrapperRef}>
      <button onClick={() => !disabled && setIsOpen(p => !p)} style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 10, padding: "6px 6px 6px 14px", width: retailWidth,
        border: `1px solid ${isOpen ? SYSTEM_COLORS.ORANGE.BURNT_ORANGE : SYSTEM_COLORS.GRAY.GAINSBORO}`, borderRadius: "10px",
        background: SYSTEM_COLORS.WHITE.WHITE, cursor: disabled ? "not-allowed" : "pointer", fontSize: "14px", color: SYSTEM_COLORS.GRAY.ONYX,
        outline: "none", whiteSpace: "nowrap", transition: "border-color 0.15s" }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0, textAlign: "left" }}>
          {selectedLabels || ""}
        </span>
        <span style={{
          width: 26, height: 26, borderRadius: "50%", flexShrink: 0,
          background: SYSTEM_COLORS.ORANGE.BURNT_ORANGE,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          transform: isOpen ? "rotate(180deg)" : "none",
          transition: "transform 0.15s" }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={SYSTEM_COLORS.WHITE.WHITE} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>

      {isOpen && createPortal(
        <div style={{
          position: "fixed", backgroundColor: SYSTEM_COLORS.WHITE.WHITE, border: `1px solid ${SYSTEM_COLORS.GRAY.SILVER}`,
          borderRadius: "14px", boxShadow: `0 8px 24px ${SYSTEM_COLORS.BLACK.BLACK_12_PERCENT}`,
          zIndex: 9999, minWidth: 240, maxHeight: 300, overflowY: "auto", padding: "6px 0",
          opacity: panelVisible ? 1 : 0,
          transform: panelVisible ? "translateY(0)" : "translateY(-4px)",
          transition: "opacity 0.12s ease-out, transform 0.12s ease-out",
        }}
          ref={el => {
            portalPanelRef.current = el;
            if (el && wrapperRef.current) {
              const btn = wrapperRef.current.querySelector("button");
              if (btn) {
                const r = btn.getBoundingClientRect();
                el.style.top = (r.bottom + 6) + "px";
                el.style.left = r.left + "px";
                el.style.minWidth = r.width + "px";
              }
            }
          }}
        >
          {options.map((opt) => {
            const isActive = selectedValues.includes(opt.value);
            const isAll = opt.value === 0;
            const isHovered = hoveredOption === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => toggle(opt.value)}
                onMouseEnter={() => setHoveredOption(opt.value)}
                onMouseLeave={() => setHoveredOption(prev => prev === opt.value ? null : prev)}
                style={{
                  width: "100%", display: "flex", alignItems: "center", gap: 12,
                  padding: "11px 16px",
                  background: isActive ? SYSTEM_COLORS.WHITE.SEASHELL : (isHovered ? SYSTEM_COLORS.WHITE.WHITE_SMOKE : "none"),
                  border: "none", cursor: "pointer", fontSize: "0.875rem",
                  color: isActive ? SYSTEM_COLORS.RED.CORAL_RED : SYSTEM_COLORS.BLACK.JET,
                  fontWeight: isAll || isActive ? "700" : "400", textAlign: "left",
                  borderBottom: isAll ? `1px solid ${SYSTEM_COLORS.GRAY.COOL_GRAY}` : "none",
                  transition: "background-color 0.1s",
                }}>
                <span style={{
                  width: 18, height: 18, borderRadius: "5px",
                  border: `2px solid ${isActive ? SYSTEM_COLORS.RED.CORAL_RED : SYSTEM_COLORS.GRAY.SILVER_SAND}`,
                  backgroundColor: isActive ? SYSTEM_COLORS.RED.CORAL_RED : "transparent",
                  display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  {isActive && (
                    <svg width="11" height="11" viewBox="0 0 10 10" fill="none">
                      <path d="M1.5 5l2.5 2.5 4.5-4.5" stroke={SYSTEM_COLORS.WHITE.WHITE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </span>
                {opt.label}
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
});
